import { columnDestinationHelpContent, HelpTooltip } from '@front/components/HelpTooltip';
import { Checkbox, List, ListBody, ListCell, ListHead, ListHeadCell, ListRow, Select, Tooltip } from '@plaidev/sour-react';
import type { ReactElement } from 'react';
import { hasRuleKind, toggleRuleKind } from '../../transformRules';
import { typeOptions } from '../constants';
import { useColumnEditor } from '../context/ColumnEditorContext';
import { ColumnOptionsMenu } from './ColumnOptionsMenu';
import { DestinationChips } from './DestinationChips';

export function ColumnSpreadsheet(): ReactElement {
  const {
    //
    filteredColumns,
    filteredAllSelected,
    toggleSelectAllFiltered,
    selectedNames,
    toggleRowSelection,
    updateColumn,
  } = useColumnEditor();

  return (
    <div className="overflow-hidden rounded-lg border border-default">
      <List stickyTop={false}>
        <ListHead>
          <ListRow>
            <ListHeadCell width={36} inextensible align="center">
              <div className="flex justify-center">
                <Checkbox checked={filteredAllSelected} onCheckedChange={(c) => toggleSelectAllFiltered(!!c)} />
              </div>
            </ListHeadCell>
            <ListHeadCell>列名 / サンプル</ListHeadCell>
            <ListHeadCell width={220}>
              <span className="inline-flex items-center gap-1">
                振り分け先
                <HelpTooltip
                  side="top"
                  ariaLabel="振り分け先の説明"
                  iconClassName="size-3.5 shrink-0 text-muted-light"
                  content={columnDestinationHelpContent}
                />
              </span>
            </ListHeadCell>
            <ListHeadCell width={110}>型</ListHeadCell>
            <ListHeadCell width={64} align="center" inextensible>
              必須
            </ListHeadCell>
            <ListHeadCell width={200} inextensible>
              <span className="inline-flex items-center gap-1">
                オプション
                <HelpTooltip
                  side="top"
                  ariaLabel="オプションの説明"
                  iconClassName="size-3.5 shrink-0 text-muted-light"
                  content="メニューからカナ正規化（小書きカナ→通常カナ）、電話番号正規化（先頭0復元・表記ゆれ除去）、複数値（同一顧客キー内で値を配列化）をオン/オフします。文字列型カラムでは前後空白の除去（TRIM）が常に有効です。複数値は「顧客」列のみ指定できます。"
                />
              </span>
            </ListHeadCell>
          </ListRow>
        </ListHead>
        <ListBody>
          {filteredColumns.map(({ col, index }) => {
            const isIgnore = col.destination === 'ignore';
            const rowSelected = selectedNames.has(col.name);
            const sampleHead = col.sampleValues.slice(0, 2);
            const sampleRest = col.sampleValues.length - sampleHead.length;
            const kanaChecked = hasRuleKind(col.transformRules, 'normalizeKana');
            const phoneChecked = hasRuleKind(col.transformRules, 'normalizePhoneNumber');
            const isCustomer = col.destination === 'customer';

            return (
              <ListRow key={col.name} selected={rowSelected} className={isIgnore ? 'opacity-60' : undefined}>
                <ListCell align="center">
                  <div className="flex justify-center">
                    <Checkbox checked={rowSelected} onCheckedChange={(c) => toggleRowSelection(col.name, !!c)} />
                  </div>
                </ListCell>
                <ListCell>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-default">{col.name}</span>
                    <div className="flex flex-wrap items-center gap-1 text-xs text-muted-light">
                      {sampleHead.length === 0 ? (
                        <span>（空が多い）</span>
                      ) : (
                        sampleHead.map((s) => (
                          <span key={s} className="inline-flex max-w-[160px] truncate rounded bg-subtle px-1.5 py-0.5 text-default">
                            {s}
                          </span>
                        ))
                      )}
                      {sampleRest > 0 ? (
                        <Tooltip
                          content={
                            <ul className="max-w-xs list-inside list-disc text-left text-xs">
                              {col.sampleValues.map((s) => (
                                <li key={s}>{s}</li>
                              ))}
                            </ul>
                          }
                        >
                          <button type="button" className="rounded bg-subtle px-1.5 py-0.5 text-action">
                            +{sampleRest}
                          </button>
                        </Tooltip>
                      ) : null}
                    </div>
                  </div>
                </ListCell>
                <ListCell>
                  <DestinationChips value={col.destination} onChange={(v) => updateColumn(index, { destination: v })} />
                </ListCell>
                <ListCell>
                  {isIgnore ? (
                    <span className="text-sm text-muted-light">—</span>
                  ) : (
                    <Select
                      variant="contained"
                      searchable={false}
                      options={typeOptions}
                      value={col.type}
                      onChange={(v) => {
                        if (v) updateColumn(index, { type: v });
                      }}
                    />
                  )}
                </ListCell>
                <ListCell align="center">
                  <div className="flex justify-center">
                    <Checkbox checked={col.required} disabled={isIgnore} onCheckedChange={(c) => updateColumn(index, { required: !!c })} />
                  </div>
                </ListCell>
                <ListCell>
                  <ColumnOptionsMenu
                    disabled={isIgnore}
                    kanaChecked={kanaChecked}
                    onToggleKana={(checked) =>
                      updateColumn(index, {
                        transformRules: toggleRuleKind(col.transformRules, 'normalizeKana', checked),
                      })
                    }
                    phoneChecked={phoneChecked}
                    onTogglePhone={(checked) =>
                      updateColumn(index, {
                        transformRules: toggleRuleKind(col.transformRules, 'normalizePhoneNumber', checked),
                      })
                    }
                    showMultiValued={isCustomer}
                    multiValuedChecked={col.multiValued}
                    onToggleMultiValued={(checked) => updateColumn(index, { multiValued: checked })}
                  />
                </ListCell>
              </ListRow>
            );
          })}
        </ListBody>
      </List>
    </div>
  );
}
